import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { getCaptainLeaderboard, getMyPredictions, getPredictionsLeaderboard } from '../api';
import { useAuth } from '../auth';
import PageHeader from '../components/PageHeader';
import CaptainPicker from '../components/CaptainPicker';

export default function Predictions() {
  const { user } = useAuth();
  const { data: predLB } = useQuery({ queryKey: ['predictions-lb'], queryFn: getPredictionsLeaderboard, refetchInterval: 60_000 });
  const { data: capLB } = useQuery({ queryKey: ['captain-lb'], queryFn: getCaptainLeaderboard, refetchInterval: 60_000 });
  const { data: mine } = useQuery({ queryKey: ['predictions-me'], queryFn: getMyPredictions, enabled: !!user });

  const predRows = predLB?.leaderboard ?? [];
  const capRows = capLB?.leaderboard ?? [];

  return (
    <>
      <PageHeader
        title="Side games"
        subtitle="Fun side games that do NOT affect the main fantasy standings."
      />
      <div className="px-4 lg:px-0 space-y-5">

        <section className="card p-4 text-xs text-dim">
          The main fantasy tournament is decided by the original scoring system applied to your 6 drafted teams.
          <br />
          The side games below — predictions and captain pick — run alongside as separate friendly competitions.
        </section>

        {/* Captain pick (interactive) */}
        <CaptainPicker />

        {/* Predictions leaderboard */}
        <section>
          <h2 className="text-xs uppercase tracking-widest text-dim mb-2">{'Prediction game leaderboard'}</h2>
          <p className="text-[11px] text-dim mb-2">{'Separate from fantasy standings. 3 pts correct outcome, +1 exact score.'}</p>
          <div className="card overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-dim border-b border-ink-800">
                  <th className="text-left py-2 px-3 font-medium w-8">#</th>
                  <th className="text-left font-medium">Player</th>
                  <th className="text-center font-medium w-12">✓</th>
                  <th className="text-center font-medium w-12">🎯</th>
                  <th className="text-right font-medium pr-3 w-12">pts</th>
                </tr>
              </thead>
              <tbody>
                {predRows.length === 0 ? (
                  <tr><td colSpan={5} className="text-center text-dim text-xs py-4">No predictions scored yet.</td></tr>
                ) : predRows.map((r, i) => (
                  <tr key={r.user} className="border-b border-ink-800/40 last:border-0">
                    <td className="py-2 px-3 stat text-dim">{i + 1}</td>
                    <td className="py-2 font-semibold">{r.user}</td>
                    <td className="text-center stat">{r.correct}</td>
                    <td className="text-center stat text-pitch-400">{r.exact}</td>
                    <td className="text-right pr-3 stat font-semibold">{r.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Captain side-game leaderboard */}
        <section>
          <h2 className="text-xs uppercase tracking-widest text-dim mb-2">Captain side-game</h2>
          <p className="text-[11px] text-dim mb-2">
            Bonus points your captain team would have doubled — earned vs expected. Picks lock when the round starts.
          </p>
          <div className="card overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-dim border-b border-ink-800">
                  <th className="text-left py-2 px-3 font-medium w-8">#</th>
                  <th className="text-left font-medium">Player</th>
                  <th className="text-center font-medium w-16">Earned</th>
                  <th className="text-right font-medium pr-3 w-16">Proj.</th>
                </tr>
              </thead>
              <tbody>
                {capRows.length === 0 ? (
                  <tr><td colSpan={4} className="text-center text-dim text-xs py-4">No picks yet.</td></tr>
                ) : capRows.map((r, i) => (
                  <tr key={r.user} className="border-b border-ink-800/40 last:border-0">
                    <td className="py-2 px-3 stat text-dim">{i + 1}</td>
                    <td className="py-2 font-semibold">{r.user}</td>
                    <td className="text-center stat">{r.earnedBonus.toFixed(0)}</td>
                    <td className="text-right pr-3 stat text-pitch-400">{(r.earnedBonus + r.expectedBonus).toFixed(1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {user && (mine?.items.length ?? 0) > 0 && (
          <section>
            <h2 className="text-xs uppercase tracking-widest text-dim mb-2">Your prediction picks</h2>
            <div className="space-y-1.5">
              {mine!.items.map((p) => (
                <Link key={p.match_id} to={`/matches/${p.match_id}`} className="card p-3 flex items-center justify-between text-sm">
                  <div>
                    <div>Match {p.match_id} · pick {p.pick}{p.home_score != null && p.away_score != null ? ` · ${p.home_score}-${p.away_score}` : ''}</div>
                    <div className="text-[10px] text-dim">{new Date(p.updated_at).toLocaleString('en-GB', { timeZone: 'Europe/Riga' })}</div>
                  </div>
                  <div className={`stat text-sm ${p.points != null && p.points > 0 ? 'text-pitch-400' : 'text-dim'}`}>
                    {p.points != null ? `${p.points} pts` : 'pending'}
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </>
  );
}
